import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, Timeline } from '@/shared/ui';

import { type TranscriptorConsultation } from '@/entities/transcriptor';

import {
  DentalPhotoAlbumGallery,
  DentalPhotoTimelineRow,
} from '@/widgets/DentalPhotoReportCard';
import {
  RawMeshViewerTimelineCard,
  RawMeshViewerTimelineRow,
} from '@/widgets/RawMeshViewerCard';
import { RecordingRow, type RecordingRowActions } from '@/widgets/RecordingRow';
import { ReportTimelineCard, ReportTimelineRow } from '@/widgets/ReportCard';

import {
  DENTAL_PHOTO_WIDGET_ID,
  i18n,
  TIMELINE_CATEGORIES,
  type TimelineCategory,
  type TimelineItem,
} from '../../config';
import { useDentalPhotoWidgetScroll } from '../../hooks';
import {
  countTimelineItemsByCategory,
  filterTimelineItems,
  getTimelineItemKey,
  groupTimelineItemsByDay,
} from '../../lib';
import { ReportCardSkeletons } from '../ReportCardSkeletons/ReportCardSkeletons';

import styles from './PatientTimeline.module.scss';

type PatientTimelineProps = {
  patientID: string;
  items: TimelineItem[];
  hiddenItems: TimelineItem[];
  showSkeletons: boolean;
  canViewArchive: boolean;
  shouldShowPhotosWidget: boolean;
  dentalPhotoCount: number;
  recordingActions: RecordingRowActions;
  userTimezone: string;
  hideMode: boolean;
  className?: string;
  isConsultationStale: (consultation: TranscriptorConsultation) => boolean;
};

export const PatientTimeline: FC<PatientTimelineProps> = (props) => {
  const {
    patientID,
    items,
    hiddenItems,
    showSkeletons,
    canViewArchive,
    shouldShowPhotosWidget,
    dentalPhotoCount,
    recordingActions,
    userTimezone,
    hideMode,
    className,
    isConsultationStale,
  } = props;

  const { formatMessage } = useIntl();
  const [category, setCategory] = useState<TimelineCategory>('all');
  const [showHidden, setShowHidden] = useState(false);

  const counts = countTimelineItemsByCategory(items);
  // The chip of an emptied category disappears, so the selection falls back
  // to "All" instead of pointing at a filter the user can no longer see.
  const activeCategory = counts[category] > 0 ? category : 'all';
  const visibleCategories = TIMELINE_CATEGORIES.filter(
    (timelineCategory) => counts[timelineCategory] > 0,
  );
  const filteredItems = filterTimelineItems(items, activeCategory);
  const filteredHiddenItems = filterTimelineItems(hiddenItems, activeCategory);

  const showHiddenSection = canViewArchive && hiddenItems.length > 0;

  useDentalPhotoWidgetScroll({
    patientID,
    shouldShowPhotosWidget,
    dentalPhotoCount,
    onBeforeScroll: () => setCategory('photos'),
  });

  const renderTimelineItem = (item: TimelineItem) => {
    switch (item.type) {
      case 'report':
        return <ReportTimelineRow structuredReportsAndStudies={item.data} />;
      case 'rawMeshViewer':
        return <RawMeshViewerTimelineRow study={item.data} />;
      case 'photoAlbum':
        return (
          <DentalPhotoTimelineRow album={item.data} patientID={patientID} />
        );
      case 'transcript':
        return (
          <RecordingRow
            consultation={item.data}
            userTimezone={userTimezone}
            hideMode={hideMode}
            isStale={isConsultationStale(item.data)}
            {...recordingActions}
          />
        );
    }
  };

  // In the photos view an album expands into a gallery inside the day card;
  // photo reports (Photo GP, Photo Ortho) stay compact rows next to it.
  const renderPhotoTimelineItem = (item: TimelineItem) =>
    item.type === 'photoAlbum' ? (
      <DentalPhotoAlbumGallery album={item.data} patientID={patientID} />
    ) : (
      renderTimelineItem(item)
    );

  // In a single-modality view the image is what the doctor looks at, so the
  // report spans the day card; 3D reports also name the scan they came from.
  const renderWideTimelineItem = (item: TimelineItem) => {
    switch (item.type) {
      case 'report':
        return (
          <ReportTimelineCard
            structuredReportsAndStudies={item.data}
            showSource={activeCategory === '3d'}
          />
        );
      case 'rawMeshViewer':
        return <RawMeshViewerTimelineCard study={item.data} />;
      default:
        return renderTimelineItem(item);
    }
  };

  const renderView = (
    viewItems: TimelineItem[],
    dataComponent: string,
    anchorID?: string,
  ) => {
    if (viewItems.length === 0) {
      return null;
    }

    switch (activeCategory) {
      case 'cbct':
      case '2d':
      case '3d':
        return (
          <Timeline
            dataComponent={dataComponent}
            groups={groupTimelineItemsByDay(viewItems)}
            getItemKey={getTimelineItemKey}
            renderItem={renderWideTimelineItem}
          />
        );
      case 'photos':
        return (
          <div id={anchorID}>
            <Timeline
              dataComponent={dataComponent}
              groups={groupTimelineItemsByDay(viewItems)}
              getItemKey={getTimelineItemKey}
              renderItem={renderPhotoTimelineItem}
            />
          </div>
        );
      default:
        return (
          <Timeline
            dataComponent={dataComponent}
            groups={groupTimelineItemsByDay(viewItems)}
            getItemKey={getTimelineItemKey}
            renderItem={renderTimelineItem}
          />
        );
    }
  };

  return (
    <section
      className={cn(styles.container, className)}
      data-section="patient-timeline"
    >
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          {visibleCategories.map((timelineCategory) => (
            <Button
              key={timelineCategory}
              variant={activeCategory === timelineCategory ? 'primary' : 'gray'}
              size="small"
              className={styles.filter}
              onClick={() => setCategory(timelineCategory)}
            >
              {formatMessage(i18n.timelineCategoryMessages[timelineCategory])}
              <span className={styles.filterCount}>
                {counts[timelineCategory]}
              </span>
            </Button>
          ))}
        </div>
      </div>

      {showSkeletons && <ReportCardSkeletons quantity={2} />}

      {!showSkeletons &&
        renderView(filteredItems, 'patient-timeline', DENTAL_PHOTO_WIDGET_ID)}

      {showHiddenSection && (
        <div className={styles.hiddenSection}>
          <Button
            variant="tertiary"
            icon={showHidden ? 'eye' : 'eyeClosed'}
            onClick={() => setShowHidden((current) => !current)}
            className={styles.showHiddenButton}
          >
            {showHidden
              ? formatMessage({
                  id: 'patientProfile.timeline.hideHiddenItems',
                  defaultMessage: 'Collapse archived',
                })
              : formatMessage(
                  {
                    id: 'patientProfile.timeline.showHiddenItems',
                    defaultMessage: 'Show archived ({count, number})',
                  },
                  { count: hiddenItems.length },
                )}
          </Button>

          {showHidden &&
            renderView(filteredHiddenItems, 'patient-timeline-archived')}
        </div>
      )}
    </section>
  );
};
